/**
 * Extracts all spans from a CQA work item's invoices structure
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @returns Array of all spans found across all invoices, transactions, and code types
 */
export const getAllSpans = (
  invoices: Accelerate.CQAInvoice[]
): Accelerate.SpanAnnotation[] => {
  const allSpans: Accelerate.SpanAnnotation[] = [];

  invoices.forEach(invoice => {
    invoice.transactions.forEach(transaction => {
      // Extract spans from procedures
      transaction.procedures.forEach(procedure => {
        if (procedure.spans) {
          allSpans.push(...procedure.spans);
        }
      });

      // Extract spans from diagnoses
      transaction.diagnoses.forEach(diagnosis => {
        if (diagnosis.spans) {
          allSpans.push(...diagnosis.spans);
        }
      });

      // Extract spans from modifiers
      transaction.modifiers.forEach(modifier => {
        if (modifier.spans) {
          allSpans.push(...modifier.spans);
        }
      });
    });
  });

  return allSpans;
};

/**
 * Merge attributes for spans that overlap with identical begin/end offsets.
 * If two or more spans share the same [beginOffset, endOffset], their attributes are concatenated
 * into a single span instance. Order of attributes is preserved by invoice traversal order.
 */
export const mergeOverlappingSpanAttributes = (
  spans: Accelerate.SpanAnnotation[]
): Accelerate.SpanAnnotation[] => {
  if (!spans || spans.length === 0) return [];

  const map = new Map<string, Accelerate.SpanAnnotation>();

  for (const span of spans) {
    const key = `${span.beginOffset}:${span.endOffset}`;
    const existing = map.get(key);
    if (existing) {
      const existingAttrs = Array.isArray(existing.attributes)
        ? existing.attributes
        : [];
      const newAttrs = Array.isArray(span.attributes) ? span.attributes : [];
      const existingRubricAttrs = Array.isArray(existing.rubricAttributes)
        ? existing.rubricAttributes
        : [];
      const newRubricAttrs = Array.isArray(span.rubricAttributes)
        ? span.rubricAttributes
        : [];

      // Create a merged span preserving first seen id/coveredText; append attributes
      map.set(key, {
        ...existing,
        attributes: [...existingAttrs, ...newAttrs],
        ...(existingRubricAttrs.length || newRubricAttrs.length
          ? { rubricAttributes: [...existingRubricAttrs, ...newRubricAttrs] }
          : {})
      });
    } else {
      map.set(key, {
        ...span,
        attributes: Array.isArray(span.attributes) ? span.attributes : []
      });
    }
  }

  // Return in stable order based on beginOffset, then endOffset
  return Array.from(map.values()).sort((a, b) => {
    if (a.beginOffset !== b.beginOffset) {
      return a.beginOffset - b.beginOffset;
    }
    return a.endOffset - b.endOffset;
  });
};

/**
 * Extracts all spans from a specific code type within invoices
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @param codeType - The type of code to extract spans from ('procedure', 'diagnosis', 'modifier')
 * @returns Array of spans from the specified code type
 */
export const getSpansByCodeType = (
  invoices: Accelerate.CQAInvoice[],
  codeType: 'procedure' | 'diagnosis' | 'modifier'
): Accelerate.SpanAnnotation[] => {
  const spans: Accelerate.SpanAnnotation[] = [];

  invoices.forEach(invoice => {
    invoice.transactions.forEach(transaction => {
      const codes =
        transaction[
          codeType === 'procedure'
            ? 'procedures'
            : codeType === 'diagnosis'
            ? 'diagnoses'
            : 'modifiers'
        ];

      codes.forEach(code => {
        if (code.spans) {
          spans.push(...code.spans);
        }
      });
    });
  });

  return spans;
};

/**
 * Extracts spans from a specific transaction
 * @param transaction - CQA transaction containing codes
 * @returns Array of spans from the transaction
 */
export const getSpansFromTransaction = (
  transaction: Accelerate.CQATransaction
): Accelerate.SpanAnnotation[] => {
  const spans: Accelerate.SpanAnnotation[] = [];

  // Extract spans from procedures
  transaction.procedures.forEach(procedure => {
    if (procedure.spans) {
      spans.push(...procedure.spans);
    }
  });

  // Extract spans from diagnoses
  transaction.diagnoses.forEach(diagnosis => {
    if (diagnosis.spans) {
      spans.push(...diagnosis.spans);
    }
  });

  // Extract spans from modifiers
  transaction.modifiers.forEach(modifier => {
    if (modifier.spans) {
      spans.push(...modifier.spans);
    }
  });

  return spans;
};

/**
 * Extracts spans from a specific code
 * @param code - CQA code containing spans
 * @returns Array of spans from the code, or empty array if no spans
 */
export const getSpansFromCode = (
  code: Accelerate.CQACode
): Accelerate.SpanAnnotation[] => {
  return code.spans || [];
};

/**
 * Gets all spans with their associated code information
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @returns Array of objects containing span and associated code metadata
 */
export const getAllSpansWithCodeInfo = (
  invoices: Accelerate.CQAInvoice[]
): Array<{
  span: Accelerate.SpanAnnotation;
  codeType: 'procedure' | 'diagnosis' | 'modifier';
  code: Accelerate.CQACode;
  transaction: Accelerate.CQATransaction;
  invoice: Accelerate.CQAInvoice;
}> => {
  const spansWithInfo: Array<{
    span: Accelerate.SpanAnnotation;
    codeType: 'procedure' | 'diagnosis' | 'modifier';
    code: Accelerate.CQACode;
    transaction: Accelerate.CQATransaction;
    invoice: Accelerate.CQAInvoice;
  }> = [];

  invoices.forEach(invoice => {
    invoice.transactions.forEach(transaction => {
      // Process procedures
      transaction.procedures.forEach(procedure => {
        if (procedure.spans) {
          procedure.spans.forEach(span => {
            spansWithInfo.push({
              span,
              codeType: 'procedure',
              code: procedure,
              transaction,
              invoice
            });
          });
        }
      });

      // Process diagnoses
      transaction.diagnoses.forEach(diagnosis => {
        if (diagnosis.spans) {
          diagnosis.spans.forEach(span => {
            spansWithInfo.push({
              span,
              codeType: 'diagnosis',
              code: diagnosis,
              transaction,
              invoice
            });
          });
        }
      });

      // Process modifiers
      transaction.modifiers.forEach(modifier => {
        if (modifier.spans) {
          modifier.spans.forEach(span => {
            spansWithInfo.push({
              span,
              codeType: 'modifier',
              code: modifier,
              transaction,
              invoice
            });
          });
        }
      });
    });
  });

  return spansWithInfo;
};

/**
 * Finds a span by ID across all invoices, transactions, and codes
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @param spanId - The ID of the span to find
 * @returns The span if found, or null if not found
 */
export const findSpanById = (
  invoices: Accelerate.CQAInvoice[],
  spanId: number
): Accelerate.SpanAnnotation | null => {
  const spansWithInfo = getAllSpansWithCodeInfo(invoices);
  const spanInfo = spansWithInfo.find(info => info.span.id === spanId);
  return spanInfo ? spanInfo.span : null;
};

/**
 * Finds a span by ID and returns its location information
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @param spanId - The ID of the span to find
 * @returns Object containing the span and its location info, or null if not found
 */
export const findSpanWithLocation = (
  invoices: Accelerate.CQAInvoice[],
  spanId: number
): {
  span: Accelerate.SpanAnnotation;
  codeType: 'procedure' | 'diagnosis' | 'modifier';
  code: Accelerate.CQACode;
  transaction: Accelerate.CQATransaction;
  invoice: Accelerate.CQAInvoice;
} | null => {
  const spansWithInfo = getAllSpansWithCodeInfo(invoices);
  return spansWithInfo.find(info => info.span.id === spanId) || null;
};

/**
 * Finds all codes that have a specific span attached
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @param spanId - The ID of the span to find codes for
 * @returns Array of objects containing code, codeType, transaction, and invoice for each code that has the span
 */
export const findCodesBySpanId = (
  invoices: Accelerate.CQAInvoice[],
  spanId: number
): Array<{
  code: Accelerate.CQACode;
  codeType: 'procedure' | 'diagnosis' | 'modifier';
  transaction: Accelerate.CQATransaction;
  invoice: Accelerate.CQAInvoice;
}> => {
  const spansWithInfo = getAllSpansWithCodeInfo(invoices);
  return spansWithInfo
    .filter(info => info.span.id === spanId)
    .map(info => ({
      code: info.code,
      codeType: info.codeType,
      transaction: info.transaction,
      invoice: info.invoice
    }));
};

/**
 * Extract ALL codes from invoices
 * Each instance of a code is kept separate
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @param codeDescriptions - Optional map of code descriptions
 * @returns Array of extracted codes with metadata
 */
export const extractCodesFromInvoices = (
  invoices: Accelerate.CQAInvoice[], 
  codeDescriptions: CMxAPI.CodeResponse = {}
): CQA.ExtractedCode[] => {
  const codes: CQA.ExtractedCode[] = [];

  if (!invoices || !Array.isArray(invoices)) return codes;

  invoices.forEach((invoice) => {
    const invoiceId = invoice.id;

    invoice.transactions?.forEach((transaction) => {
      const transactionId = transaction.id;

      // Extract procedures - each instance is separate
      transaction.procedures?.forEach((proc, procIdx) => {
        if (proc.code) {
          const codeInfo = codeDescriptions[proc.code];
          codes.push({
            ...proc, // Spread all properties from CQACodeCommon (includes spans array)
            codeId: proc.id,
            codeType: 'procedure',
            value: proc.code,
            compositeId: `inv-${invoiceId}-tx-${transactionId}-proc-${proc.id}-${procIdx}`,
            description: codeInfo?.description || proc.description || '',
            invoiceId,
            transactionId,
            spanId: proc.spans?.[0]?.id || 0, // Deprecated: kept for backward compatibility
            spans: proc.spans || [], // Array of all spans this code is attached to
          });
        }
      });

      // Extract modifiers - each instance is separate
      transaction.modifiers?.forEach((mod, modIdx) => {
        if (mod.code) {
          const codeInfo = codeDescriptions[mod.code];
          codes.push({
            ...mod, // Spread all properties from CQACodeCommon (includes spans array)
            codeId: mod.id,
            codeType: 'modifier',
            value: mod.code,
            compositeId: `inv-${invoiceId}-tx-${transactionId}-mod-${mod.id}-${modIdx}`,
            description: codeInfo?.description || mod.description || '',
            invoiceId,
            transactionId,
            spanId: mod.spans?.[0]?.id || 0, // Deprecated: kept for backward compatibility
            spans: mod.spans || [], // Array of all spans this code is attached to
          });
        }
      });

      // Extract diagnoses - each instance is separate
      transaction.diagnoses?.forEach((diag, diagIdx) => {
        if (diag.code) {
          const codeInfo = codeDescriptions[diag.code];
          codes.push({
            ...diag, // Spread all properties from CQACodeCommon (includes spans array)
            codeId: diag.id,
            codeType: 'diagnosis',
            value: diag.code,
            compositeId: `inv-${invoiceId}-tx-${transactionId}-diag-${diag.id}-${diagIdx}`,
            description: codeInfo?.description || diag.description || '',
            invoiceId,
            transactionId,
            spanId: diag.spans?.[0]?.id || 0, // Deprecated: kept for backward compatibility
            spans: diag.spans || [], // Array of all spans this code is attached to
          });
        }
      });
    });
  });

  return codes;
};

/**
 * Calculate available codes by removing used codes from invoice codes
 * Removes one instance of each used code (preserves duplicates)
 * @param allInvoiceCodes - All codes extracted from invoices
 * @param usedCodes - Codes that are already assigned to spans
 * @returns Array of available codes that can still be assigned
 * @deprecated Use direct filtering in components instead. This function removes codes attached to ANY span,
 * but codes should remain available for other spans. Use: allInvoiceCodes.filter(code => !code.spans?.some(span => span.id === selectedSpanId))
 */
export const calculateAvailableCodes = (
  allInvoiceCodes: CQA.ExtractedCode[],
  usedCodes: CQA.ExtractedCode[]
): CQA.ExtractedCode[] => {
  // Create a copy to avoid mutating original
  const availableCodes = [...allInvoiceCodes];

  // Create a map to track how many times each code is used
  const usedCodeCounts = new Map<string, number>();
  usedCodes.forEach((used) => {
    const key = `${used.codeType}-${used.value}`;
    usedCodeCounts.set(key, (usedCodeCounts.get(key) || 0) + 1);
  });

  // Remove used codes (one instance per usage)
  const filteredCodes: CQA.ExtractedCode[] = [];
  const removedCounts = new Map<string, number>();

  availableCodes.forEach((code) => {
    const key = `${code.codeType}-${code.value}`;
    const usedCount = usedCodeCounts.get(key) || 0;
    const alreadyRemoved = removedCounts.get(key) || 0;

    if (alreadyRemoved < usedCount) {
      // This instance should be removed (already used)
      removedCounts.set(key, alreadyRemoved + 1);
    } else {
      // This instance is available
      filteredCodes.push(code);
    }
  });

  return filteredCodes;
};

/**
 * Calculate unattached codes (codes not attached to any span)
 * @param allInvoiceCodes - All codes extracted from invoices
 * @returns Array of codes that are not attached to any span
 * @deprecated Use direct filtering in components instead. Filter by selectedSpanId to allow codes attached to other spans.
 */
export const calculateUnattachedCodes = (
  allInvoiceCodes: CQA.ExtractedCode[]
): CQA.ExtractedCode[] => {
  return allInvoiceCodes.filter(code => !code.spans || code.spans.length === 0);
};

const getAttachedCodes = (exractedCodes: CQA.ExtractedCode[]): CQA.AttachedCodes => {
  return {
    procedures: exractedCodes.filter(code => code.codeType === 'procedure'),
    modifiers: exractedCodes.filter(code => code.codeType === 'modifier'),
    diagnoses: exractedCodes.filter(code => code.codeType === 'diagnosis'),
  };
};

/**
 * Calculate text selection offsets from DOM selection
 * Handles offset calculation, whitespace trimming, and word boundary snapping
 * @param container - The container element containing the text
 * @param selection - The window Selection object
 * @param range - The Range object from the selection
 * @param text - The full text content of the document
 * @returns Object with startIndex, endIndex, and selectedText, or null if invalid
 */
export const calculateTextSelectionOffsets = (
  container: HTMLElement,
  selection: Selection,
  range: Range,
  text: string
): { startIndex: number; endIndex: number; selectedText: string } | null => {
  if (!selection || selection.toString().length === 0) {
    return null;
  }

  // Calculate absolute positions in the document
  const calculateAbsoluteOffset = (node: Node, offset: number): number => {
    let absoluteOffset = 0;
    let currentPos = 0;
    
    // Walk through all text nodes to find the target node and calculate position
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    let textNode;
    while (textNode = walker.nextNode()) {
      const textContent = textNode.textContent || '';
      
      if (textNode === node) {
        // Found our node, add the offset within this node
        absoluteOffset = currentPos + offset;
        break;
      }
      
      // Add the full length of this text node
      currentPos += textContent.length;
    }
    
    return absoluteOffset;
  };

  // Calculate absolute start and end positions
  let startIndex = calculateAbsoluteOffset(range.startContainer, range.startOffset);
  let endIndex = calculateAbsoluteOffset(range.endContainer, range.endOffset);

  // trim leading whitespace
  while (startIndex < endIndex && /\s/.test(text[startIndex])) {
    startIndex++;
  }

  // trim trailing whitespace
  while (endIndex > startIndex && /\s/.test(text[endIndex - 1])) {
    endIndex--;
  }

  // Now snap to word boundaries only if we're in the middle of a word
  // Expand backwards to start of word only if current position is mid-word
  if (startIndex > 0 && /\S/.test(text[startIndex]) && /\S/.test(text[startIndex - 1])) {
    while (startIndex > 0 && /\S/.test(text[startIndex - 1])) {
      startIndex--;
    }
  }
  
  // Expand forwards to end of word only if current position is mid-word
  if (endIndex < text.length && /\S/.test(text[endIndex - 1]) && /\S/.test(text[endIndex])) {
    while (endIndex < text.length && /\S/.test(text[endIndex])) {
      endIndex++;
    }
  }

  // Get the final selected text after snapping and trimming
  const finalSelectedText = text.slice(startIndex, endIndex);

  // Only proceed if we have a valid selection after trimming
  if (finalSelectedText.length === 0) {
    return null;
  }

  return {
    startIndex,
    endIndex,
    selectedText: finalSelectedText
  };
};

/**
 * Maps an ExtractedCode to SuggestedCode format
 * @param code - The extracted code to convert
 * @returns A SuggestedCode object compatible with code suggestion UI components
 */
export const mapExtractedCodetoSuggestedCode = (code: CQA.ExtractedCode): CMxCommonApp.SuggestedCode => ({
  code: code.value,
  codeType: code.codeType === 'procedure' ? 'CPT' : code.codeType === 'diagnosis' ? 'ICD' : 'MODIFIER',
  description: code.description || '',
  code_source: 'CURRENT_CASE',
  code_recent_date: new Date().toISOString(),
  suggested_code_type: 'CURRENT_CASE',
  transaction_guids: code?.spans?.map(span => span?.id?.toString()),
  effectiveDate: '',
  shortDescription: code.description || '',
  tenantDefined: false,
  codeId: code.codeId
});
